import { useCallback, useEffect, useRef, useState } from "react";
import WebApp from "@twa-dev/sdk";
import { useTranslation } from "react-i18next";
import { GoogleAuthProvider, OAuthProvider, signInWithPopup } from "firebase/auth";
import { firebaseAuth } from "../../utils/firebase";
import { BeeMark, Banner, LangSwitch, ThemeToggle, Icon } from "../common/ui";
import { createLoginRequest, declineLoginRequest, pollLoginRequest, previewInvite, type LoginKind } from "../../utils/api";
import type { InvitePreview } from "../../types";

interface Props {
  onLoginFirebase: (idToken: string) => Promise<void>;
  /** A session obtained by approval on the other side (bot / Google browser). */
  onToken: (accessToken: string) => void;
  /** Inside the Telegram Mini App after signing out: Telegram is one tap, and
   *  Google/Apple go through the real browser (Google blocks in-app webviews). */
  inTelegram?: boolean;
  onTelegramLogin?: () => Promise<void>;
  /** Code from an invite link the app was opened with — previewed, not redeemed here. */
  inviteCode?: string | null;
  theme: "dark" | "light";
  onThemeChange: (t: "dark" | "light") => void;
}

function GoogleMark() {
  return (
    <svg width="19" height="19" viewBox="0 0 24 24" aria-hidden>
      <path fill="#4285F4" d="M21.6 12.2c0-.7-.1-1.3-.2-2H12v3.8h5.4a4.6 4.6 0 0 1-2 3v2.5h3.2c1.9-1.7 3-4.3 3-7.3Z" />
      <path fill="#34A853" d="M12 22c2.7 0 5-.9 6.6-2.4l-3.2-2.5c-.9.6-2 1-3.4 1-2.6 0-4.8-1.7-5.6-4.1H3.1v2.6A10 10 0 0 0 12 22Z" />
      <path fill="#FBBC05" d="M6.4 14c-.2-.6-.3-1.3-.3-2s.1-1.4.3-2V7.4H3.1a10 10 0 0 0 0 9.2L6.4 14Z" />
      <path fill="#EA4335" d="M12 5.9c1.5 0 2.8.5 3.8 1.5l2.8-2.8A10 10 0 0 0 3.1 7.4L6.4 10c.8-2.4 3-4.1 5.6-4.1Z" />
    </svg>
  );
}

function AppleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M16.4 12.7c0-2.4 2-3.5 2-3.6-1.1-1.6-2.8-1.8-3.4-1.8-1.5-.2-2.8.8-3.6.8-.7 0-1.9-.8-3.1-.8-1.6 0-3 .9-3.9 2.4-1.6 2.9-.4 7.1 1.2 9.4.8 1.1 1.7 2.4 2.9 2.3 1.2 0 1.6-.7 3-.7s1.8.7 3 .7 2-1.1 2.8-2.2c.9-1.3 1.2-2.5 1.3-2.6-.1 0-2.5-1-2.5-3.8Zm-2.4-7c.6-.8 1-1.9.9-3-.9 0-2 .6-2.7 1.4-.6.7-1.1 1.8-.9 2.8 1 .1 2-.5 2.7-1.2Z" />
    </svg>
  );
}

/**
 * What an invite link gives, shown above the sign-in buttons so a new visitor
 * knows why to create an account. Redemption itself happens after sign-in, in
 * App; this only reads the public preview.
 */
function InviteCard({ code }: { code: string }) {
  const { t, i18n } = useTranslation();
  const isRu = !i18n.language?.startsWith("en");
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    let alive = true;
    previewInvite(code)
      .then((p) => { if (alive) setPreview(p); })
      .catch(() => { if (alive) setMissing(true); });
    return () => { alive = false; };
  }, [code]);

  if (missing) return <Banner kind="error">{t("invite.err.INVITE_NOT_FOUND")}</Banner>;
  if (!preview) return null;
  if (preview.status !== "active") {
    const key = preview.status === "redeemed" ? "INVITE_USED"
      : preview.status === "revoked" ? "INVITE_REVOKED" : "INVITE_EXPIRED";
    return <Banner kind="error">{t(`invite.err.${key}`)}</Banner>;
  }
  return (
    <div className="invite-card" role="status">
      <span className="invite-card__icon"><Icon name="ticket" size={18} stroke={2.1} /></span>
      <span style={{ minWidth: 0 }}>
        <span className="t-sm" style={{ fontWeight: 800, display: "block" }}>{t("invite.loginTitle")}</span>
        <span className="t-sm" style={{ display: "block", marginTop: 2 }}>
          {preview.blocks.map((b) => (isRu ? b.title_ru : b.title_en)).join(", ")}
          {" · "}{t("invite.days", { count: preview.days })}
        </span>
        <span className="t-xs muted-2" style={{ display: "block", marginTop: 4 }}>{t("invite.loginHint")}</span>
      </span>
    </div>
  );
}

type Wait = { kind: LoginKind; code: string; check: string; link?: string };

export default function LoginView({ onLoginFirebase, onToken, inTelegram = false, onTelegramLogin, inviteCode, theme, onThemeChange }: Props) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [wait, setWait] = useState<Wait | null>(null);

  // Mini App → Google: Telegram opens an external link only straight from a
  // tap, so the request is made AHEAD of the tap and renewed before it expires.
  const [ready, setReady] = useState<Wait | null>(null);
  const prepare = useCallback(() => {
    createLoginRequest("miniapp_via_google")
      .then((r) => setReady({ kind: "miniapp_via_google", code: r.code, check: r.check }))
      .catch(() => setReady(null));
  }, []);
  useEffect(() => {
    if (!inTelegram) return;
    prepare();
    const id = setInterval(prepare, 8 * 60 * 1000);
    return () => clearInterval(id);
  }, [inTelegram, prepare]);

  // While waiting: ask every few seconds whether the other side approved.
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;
  useEffect(() => {
    if (!wait) return;
    let stopped = false;
    const tick = async () => {
      try {
        const r = await pollLoginRequest(wait.code);
        if (stopped) return;
        if (r.access_token) { stopped = true; onTokenRef.current(r.access_token); }
      } catch (e) {
        if (stopped) return;
        if (String(e).includes("410")) { stopped = true; setWait(null); setError(t("auth.loginExpired")); }
      }
    };
    const id = setInterval(tick, 2500);
    return () => { stopped = true; clearInterval(id); };
  }, [wait, t]);

  const telegramFromBrowser = async () => {
    setError(null);
    // Opened inside the tap, then pointed at the bot once the link exists, so
    // a popup blocker does not eat it; the panel keeps a link as a fallback.
    const win = window.open("", "_blank");
    try {
      const r = await createLoginRequest("browser_via_telegram");
      setWait({ kind: "browser_via_telegram", code: r.code, check: r.check, link: r.link });
      if (win && r.link) win.location.href = r.link; else win?.close();
    } catch {
      win?.close();
      setError(t("auth.errGeneric"));
    }
  };

  const googleFromTelegram = () => {
    if (!ready) { prepare(); return; }
    const url = `${window.location.origin}/?approve=${encodeURIComponent(ready.code)}`;
    try { WebApp.openLink(url); } catch { window.open(url, "_blank", "noopener"); }
    setWait(ready);
    setReady(null);
  };

  const cancelWait = () => {
    if (wait) declineLoginRequest(wait.code).catch(() => {});
    setWait(null);
    if (inTelegram) prepare();
  };

  const telegramInApp = async () => {
    if (!onTelegramLogin) return;
    setError(null);
    setLoading(true);
    try { await onTelegramLogin(); } catch { setError(t("auth.errGeneric")); }
    finally { setLoading(false); }
  };

  // Closing the popup or triggering a second one isn't a real error — the user
  // just backed out, so we show nothing. Everything else maps to a friendly,
  // localized message instead of Firebase's raw "Firebase: Error (auth/…)".
  const CANCELLED = new Set([
    "auth/popup-closed-by-user",
    "auth/cancelled-popup-request",
    "auth/user-cancelled",
  ]);
  const errorMessage = (code: string): string => {
    switch (code) {
      case "auth/popup-blocked": return t("auth.errPopupBlocked");
      case "auth/unauthorized-domain": return t("auth.errUnauthorizedDomain");
      case "auth/operation-not-allowed": return t("auth.errProviderDisabled");
      case "auth/network-request-failed": return t("auth.errNetwork");
      default: return t("auth.errGeneric");
    }
  };

  const withFirebase = async (getToken: () => Promise<string>) => {
    setError(null);
    setLoading(true);
    try {
      await onLoginFirebase(await getToken());
    } catch (err: any) {
      const code = err?.code || "";
      if (!CANCELLED.has(code)) setError(errorMessage(code));
    } finally {
      setLoading(false);
    }
  };

  const signInGoogle = () =>
    withFirebase(async () => {
      const r = await signInWithPopup(firebaseAuth, new GoogleAuthProvider());
      return r.user.getIdToken();
    });

  const signInApple = () =>
    withFirebase(async () => {
      const provider = new OAuthProvider("apple.com");
      provider.addScope("email");
      provider.addScope("name");
      const r = await signInWithPopup(firebaseAuth, provider);
      return r.user.getIdToken();
    });

  return (
    <div className="beeapp__scroll">
      <div style={{ minHeight: "100%", display: "flex", flexDirection: "column", padding: 18 }}>
        {/* top utility bar */}
        <div className="between" style={{ marginBottom: 14 }}>
          <LangSwitch />
          <ThemeToggle theme={theme} onThemeChange={onThemeChange} />
        </div>

        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div
            className="glass glass-lg glass-sheen login-card anim-rise"
            style={{ width: "100%", maxWidth: 400, padding: 0, overflow: "hidden" }}
          >
            <div className="login-split">
              {/* Brand panel — desktop only */}
              <div
                className="login-brand"
                style={{
                  padding: 36,
                  background: "linear-gradient(165deg, var(--accent-tint), var(--violet-tint))",
                  borderRight: "1px solid var(--border)",
                }}
              >
                <div className="row" style={{ gap: 12 }}>
                  <BeeMark size={46} />
                  <div>
                    <div className="t-title">{t("app.title")}</div>
                    <div className="t-sm muted">{t("app.tagline")}</div>
                  </div>
                </div>
                <div>
                  <p className="t-display" style={{ marginBottom: 10 }}>{t("auth.welcomeBack")}</p>
                  <p className="t-body muted">{t("menu.startSelectionDesc")}</p>
                </div>
                <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                  <span className="badge badge-amber"><Icon name="droplet" size={12} /> Pigment</span>
                  <span className="badge badge-violet"><Icon name="graduation" size={12} /> Study</span>
                </div>
              </div>

              {/* Sign-in panel */}
              <div style={{ padding: 28 }}>
                <div className="hide-wide" style={{ textAlign: "center", marginBottom: 22 }}>
                  <div style={{ display: "inline-flex" }}><BeeMark size={56} /></div>
                  <div className="t-title" style={{ marginTop: 12 }}>{t("app.title")}</div>
                  <div className="t-sm muted">{t("auth.welcomeBack")}</div>
                </div>
                <div className="only-wide" style={{ marginBottom: 18 }}>
                  <div className="t-h">{t("auth.login")}</div>
                </div>

                {inviteCode && <div style={{ marginBottom: 14 }}><InviteCard code={inviteCode} /></div>}

                {error && <div style={{ marginBottom: 14 }}><Banner kind="error">{error}</Banner></div>}

                {wait ? (
                  <div className="cross-login" role="status">
                    <p className="t-h" style={{ margin: 0 }}>
                      {wait.kind === "browser_via_telegram" ? t("auth.waitTelegramTitle") : t("auth.waitGoogleTitle")}
                    </p>
                    <p className="t-sm muted" style={{ margin: "6px 0 10px", lineHeight: 1.45 }}>
                      {wait.kind === "browser_via_telegram" ? t("auth.waitTelegramText") : t("auth.waitGoogleText")}
                    </p>
                    <div className="cross-login__check">{wait.check}</div>
                    {wait.link && (
                      <a className="btn btn-primary btn-block" href={wait.link} target="_blank" rel="noopener noreferrer"
                        style={{ textDecoration: "none", marginTop: 12 }}>
                        <Icon name="send" size={16} /> {t("auth.openTelegram")}
                      </a>
                    )}
                    {wait.kind === "miniapp_via_google" && (
                      <button type="button" className="btn btn-primary btn-block" style={{ marginTop: 12 }}
                        onClick={() => {
                          const url = `${window.location.origin}/?approve=${encodeURIComponent(wait.code)}`;
                          try { WebApp.openLink(url); } catch { window.open(url, "_blank", "noopener"); }
                        }}>
                        <Icon name="globe" size={16} /> {t("auth.openBrowserAgain")}
                      </button>
                    )}
                    <p className="t-xs muted-2" style={{ margin: "12px 0 0", display: "flex", alignItems: "center", gap: 8, justifyContent: "center" }}>
                      <span className="spinner" style={{ width: 14, height: 14 }} /> {t("auth.waiting")}
                    </p>
                    <button type="button" className="btn btn-glass btn-block" style={{ marginTop: 10 }} onClick={cancelWait}>
                      {t("auth.cancel")}
                    </button>
                  </div>
                ) : inTelegram ? (
                  <div className="stack" style={{ "--gap": "10px" } as React.CSSProperties}>
                    <button type="button" className="btn btn-primary btn-block" onClick={telegramInApp} disabled={loading}>
                      {loading ? <span className="spinner" /> : <><Icon name="send" size={17} /> {t("auth.telegramLogin")}</>}
                    </button>
                    <p className="t-xs muted-2" style={{ margin: "-2px 0 4px", textAlign: "center" }}>{t("auth.telegramAccountHint")}</p>
                    <button type="button" className="btn btn-glass btn-block" onClick={googleFromTelegram} disabled={loading}>
                      <GoogleMark /> {t("auth.googleFromTelegram")}
                    </button>
                    <p className="t-xs muted-2" style={{ margin: "-2px 0 0", textAlign: "center", lineHeight: 1.4 }}>{t("auth.googleFromTelegramHint")}</p>
                  </div>
                ) : (
                  <div className="stack" style={{ "--gap": "10px" } as React.CSSProperties}>
                    <button type="button" className="btn btn-glass btn-block" onClick={signInGoogle} disabled={loading}>
                      {loading ? <span className="spinner" /> : <><GoogleMark /> {t("auth.googleLogin")}</>}
                    </button>
                    <button type="button" className="btn btn-glass btn-block" onClick={signInApple} disabled={loading}>
                      {loading ? <span className="spinner" /> : <><AppleMark /> {t("auth.appleLogin")}</>}
                    </button>
                    <button type="button" className="btn btn-glass btn-block" onClick={telegramFromBrowser} disabled={loading}>
                      <Icon name="send" size={17} /> {t("auth.telegramLogin")}
                    </button>
                  </div>
                )}

                <p className="t-xs muted-2" style={{ textAlign: "center", marginTop: 18, lineHeight: 1.5, fontWeight: 600 }}>
                  {t("auth.terms")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
